import {
  useGetUploadSignedUrlImportQuestionSpreadsheetLazyQuery,
  useImportQuestionSpreadsheetMutation,
  SearchQuestionsDocument,
  QuestionGroupType,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  type DialogProps,
  CloseRoundedIcon,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useMemo, useState } from 'react';

import { QContentImportForm, type QContentImportFormInput } from '../../components';
import { QuestionCategoryInput } from '../../constants';
import { useQContentSearchParams } from '../../hooks';

interface QContentImportModalProps extends DialogProps {
  onClose?: () => void;
  open: boolean;
  singleImportTemplateUrl: string;
  itemSetImportTemplateUrl: string;
}

const CATEGORY_SEARCH_OPTIONS = {
  [QuestionCategoryInput.SINGLE_AND_LOQ]: [
    QuestionGroupType.Single,
    QuestionGroupType.LotOfQuestion,
  ],
  [QuestionCategoryInput.ITEM_SET]: [QuestionGroupType.ItemSet],
};

export default function QContentImportModal(props: Readonly<QContentImportModalProps>) {
  const { onClose, open: isOpen, singleImportTemplateUrl, itemSetImportTemplateUrl } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { qBankStructureInformation } = useQBankStructure();
  const [
    {
      page,
      pageSize,
      sort,
      category,
      qIds,
      part,
      subject,
      group,
      chapter,
      topic,
      subtopic,
      status,
    },
  ] = useQContentSearchParams();

  const levelItemId = subtopic || topic || chapter || group || subject || part;
  const groupTypeSearchOptions = useMemo(() => CATEGORY_SEARCH_OPTIONS[category], [category]);

  const [getUploadSignedUrl] = useGetUploadSignedUrlImportQuestionSpreadsheetLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [importQuestionSpreadsheet, { loading: isImportLoading }] =
    useImportQuestionSpreadsheetMutation();

  const { showSnackbar } = useSnackbar();

  const handleClose = () => {
    onClose?.();
    setErrorMessage(null);
  };

  const handleSubmit = async (data: QContentImportFormInput) => {
    const { file, type } = data;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const { data: getUploadSignedUrlData } = await getUploadSignedUrl({
        variables: {
          input: {
            contentType: file.type,
            qBankStructureId: qBankStructureInformation.id,
          },
        },
      });

      if (!getUploadSignedUrlData?.qctGetUploadSignedUrlImportQuestionSpreadsheet) {
        throw new Error('Fail to get signed url');
      }

      const {
        signedUrl,
        storageObject: { fileName },
      } = getUploadSignedUrlData.qctGetUploadSignedUrlImportQuestionSpreadsheet;

      const uploadFileResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!fileName) {
        throw new Error('Invalid file name');
      }

      if (!uploadFileResponse.ok) {
        throw new Error('Fail to import Questions');
      }

      await importQuestionSpreadsheet({
        variables: {
          input: {
            fileName,
            qBankStructureId: qBankStructureInformation.id,
            type,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: SearchQuestionsDocument,
            variables: {
              search: {
                refQBankStructureId: qBankStructureInformation.id,
                refCode: qIds,
                questionGroupType: groupTypeSearchOptions ?? undefined,
                levelItemId,
                status: status ? [status] : [],
              },
              paging: {
                pageSize,
                currentPage: page,
              },
              order: {
                field: 'updatedAt',
                type: sort,
              },
            },
          },
        ],
        onCompleted() {
          showSnackbar({
            message: 'Questions Imported',
            variant: 'success',
          });
          setErrorMessage(null);
          onClose?.();
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage('นำเข้าไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} maxWidth='sm' onClose={handleClose}>
      <DialogTitle>Import Question</DialogTitle>
      <IconButton
        disabled={isImportLoading}
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
        }}
      >
        <CloseRoundedIcon />
      </IconButton>
      <Divider />
      <QContentImportForm
        onSubmit={handleSubmit}
        onClose={handleClose}
        loading={isLoading}
        error={errorMessage}
        singleImportTemplateUrl={singleImportTemplateUrl}
        itemSetImportTemplateUrl={itemSetImportTemplateUrl}
      />
    </Dialog>
  );
}
